import {
  createContext,
  useContext,
  useReducer,
  ReactNode,
  useMemo,
} from 'react';

/**
 * The state shape for Blog Generation dashboard.
 */
export interface BlogGenerationState {
  blogData: any | null;
}

/**
 * Actions supported by the Blog Generation Reducer.
 */
export enum BlogGenerationActionTypes {
  SET_CURRENT_VIEW = 'SET_CURRENT_VIEW',
  SET_DETAILED_VIEW_URL = 'SET_DETAILED_VIEW_URL',
  SET_BLOG_DATA = 'SET_BLOG_DATA'
}

type BlogGenerationAction =
  | {
      type: BlogGenerationActionTypes.SET_CURRENT_VIEW;
      payload: 'overview' | 'detailed';
    }
  | { type: BlogGenerationActionTypes.SET_DETAILED_VIEW_URL; payload: string }
  | { type: BlogGenerationActionTypes.SET_BLOG_DATA; payload: any };


const initialState: BlogGenerationState = {
  blogData: null
};

const blogGenerationReducer = (
  state: BlogGenerationState,
  action: BlogGenerationAction
): BlogGenerationState => {
  switch (action.type) {
    case BlogGenerationActionTypes.SET_BLOG_DATA:
      return { ...state, blogData: action.payload };
    default:
      return state;
  }
};

/**
 * Context value interface including state and action setters.
 */
interface BlogGenerationContextValue {
  state: BlogGenerationState;
}

export const BlogGenerationContext = createContext<
  BlogGenerationContextValue | undefined
>(undefined);

interface BlogGenerationProviderProps {
  children: ReactNode;
}

/**
 * `BlogGenerationProvider` wraps the Blog Generation dashboard to supply global page state
 * and mutations using a clean, central useReducer pattern.
 */
export const BlogGenerationProvider = ({
  children,
}: BlogGenerationProviderProps) => {
  const [state, dispatch] = useReducer(blogGenerationReducer, initialState);

  const value = useMemo(
    () => ({
      state,
    }),
    [state]
  );

  return (
    <BlogGenerationContext.Provider value={value}>
      {children}
    </BlogGenerationContext.Provider>
  );
};

/**
 * Hook to consume the BlogGenerationContext within provider children.
 */
export const useBlogGeneration = (): BlogGenerationContextValue => {
  const context = useContext(BlogGenerationContext);
  if (context === undefined) {
    throw new Error(
      'useBlogGeneration must be used within a BlogGenerationProvider'
    );
  }
  return context;
};
